import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "../../../stores/setup/use-store";
import { EnvironmentalType } from "../../../enum/EnvironmentalType";
import { message, Modal, Spin } from "antd";
import { StoreState } from "../../../enum/StoreState";
import axios, { CancelToken } from "axios";
import I4castAlert from "../../../components/I4castAlert/I4castAlert";
import ForecastChartPanelEnvironmentalButtons from "../ForecastChartPanelEnvironmentalButtons/ForecastChartPanelEnvironmentalButtons";
import ForecastChartPanelMeteogram from "../ForecastChartPanelMeteogram/ForecastChartPanelMeteogram";

import { regionToMacroRegion } from "../../../util/validadePointInsidePolygon/getGridLimitsFromConfig";
import { MacroRegion } from "../../../enum/MacroRegion";
import {
  Box,
  Flex,
  useToast,
  Text,
  useDisclosure,
  useMediaQuery,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import iconWave from "../../../assets/wave.svg";
import ForecastChartPanelHeader from "../ForecastChartPanelHeader/ForecastChartPanelHeader";
import ForecastChartInsightTimeline from "../ForecastChartInsightTimeline/ForecastChartInsightTimeline";
import { VisualizationType } from "../../../enum/VisualizationType";
import useGetStationsWithSensorData from "./hooks/useGetStationsWithSensorData";
import { getEnvironmentalVariablesDefault } from "util/meteogramCharts/processSerie/common/getEnvironmentalVariablesDefault";
import { LAST_SELECTED_ENVIRONMENTAL_TYPE_KEY } from "../../../constants";
import ForecastChartPanelMapContainer from "../ForecastChartPanelMap/ForecastChartPanelMap.container";
import { ChevronRight } from "lucide-react";
import Megaophone from "../../../assets/megaphone.svg";
import { EventCreateDrawer } from "pages/Event";
import { useLocation } from "react-router-dom";
import { useAlertPhoneVerification } from "pages/Alert/hooks/useAlertPhoneVerification";
import { PhoneVerificationModal } from "pages/Admin/MyData/components/PhoneVerificationModal";

const ForecastChartPage: React.FC = () => {
  const {
    dataStores: {
      forecastChartPageStore,
      forecastChartMeteogramStore,
      regionStore,
      unitMeasurementStore,
      forecastChartProbabilisticStore,
      analyticsChartStore,
      requestCancellationStore,
      insightAlertStore,
      locationStore,
    },
  } = useStores();

  const {
    isOpen: isPhoneModalOpen,
    onOpen: onPhoneModalOpen,
    onClose: onPhoneModalClose,
    phoneData,
    handlePhoneUpdateSuccess,
    isPhoneAlreadyVerified,
    t: tPhoneModal,
  } = useAlertPhoneVerification();

  const toast = useToast();
  const toastId = "toastInfoNoDataId";
  const location = useLocation();
  const [isSmallScreen] = useMediaQuery("(max-width: 1680px)");

  const [disableMenuItems, setDisableMenuItems] =
    useState<EnvironmentalType[]>();
  const [defaultSelectedKeys, setDefaultSelectedKeys] = useState<
    EnvironmentalType[]
  >([]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [drawerInitialStep, setDrawerInitialStep] = useState(0);

  const { t, i18n } = useTranslation("translation", {
    keyPrefix: "forecastChart.forecastChartPage",
  });

  const showInSmallScreen =
    forecastChartPageStore.insightMode &&
    !!forecastChartPageStore.selectedStation &&
    isSmallScreen;

  const preventTranslationOnRender = useRef(true);

  useGetStationsWithSensorData(
    forecastChartPageStore,
    forecastChartMeteogramStore,
  );

  // Open event drawer when returning from sensor selection (or other map selection flows)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openCreateDrawer = queryParams.get("open_create_drawer");

    if (openCreateDrawer === "true") {
      setDrawerInitialStep(1); // Step 2 (0-indexed) — returning from sensor selection
      onOpen();
      // Clean up URL parameter after opening drawer
      queryParams.delete("open_create_drawer");
      const newSearch = queryParams.toString();
      window.history.replaceState(
        {},
        "",
        `${location.pathname}${newSearch ? `?${newSearch}` : ""}`,
      );
    }
  }, [location.search, onOpen, location.pathname]);

  useEffect(() => {
    if (
      forecastChartPageStore.stations.length &&
      forecastChartPageStore.selectedStation === 0
    ) {
      const stationId = forecastChartPageStore.stations[0].station_id;
      forecastChartPageStore.setSelectedStation(stationId);
    }
    forecastChartPageStore
      .getValidStationsForecastChartAPI(EnvironmentalType.WAVE_SEA)
      .then(() => {
        if (forecastChartPageStore.stationsWithDataPostProcessed?.length) {
          let showModal = localStorage.getItem("showWaveSpectraModal");

          if (!showModal) {
            showModal = "true";
            localStorage.setItem("showWaveSpectraModal", showModal);
          }

          //TODO: Melhorar esse modal
          if (
            showModal === "true" &&
            forecastChartPageStore.tabStateActivty === EnvironmentalType.WAVE
          )
            Modal.info({
              title:
                t("clickBlueMarkersInTheMap") +
                t("toDisplayForecastChartWaveSpectra"),

              icon: <img src={iconWave} alt="wave" />,

              okText: t("letsGo"),
              onOk: handleHideWaveSpectraModal,
            });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (forecastChartPageStore.tabStateActivty !== EnvironmentalType.WAVE) {
      return;
    }

    if (!forecastChartPageStore.stationsWithDataPostProcessed?.length) {
      return;
    }

    let showModal = localStorage.getItem("showWaveSpectraModal");

    if (!showModal) {
      showModal = "true";
      localStorage.setItem("showWaveSpectraModal", showModal);
    }

    if (
      showModal === "true" &&
      forecastChartPageStore.tabStateActivty === EnvironmentalType.WAVE
    )
      Modal.info({
        title:
          t("clickBlueMarkersInTheMap") +
          t("toDisplayForecastChartWaveSpectra"),

        icon: <img src={iconWave} alt="wave" />,

        okText: t("letsGo"),
        onOk: handleHideWaveSpectraModal,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastChartPageStore.tabStateActivty, t]);

  useEffect(() => {
    forecastChartPageStore.setFavoriteInsightAlreadyLoaded(false);
    unitMeasurementStore.getUserUnitMeasurementApi();

    const queryParams = new URLSearchParams(location.search);
    const alertId = queryParams.get("alert_id");
    const locationId = queryParams.get("location_id");
    const operationRuleLocationId = queryParams.get(
      "operation_rule_location_id",
    );
    const eventLocationId = queryParams.get("event_location_id");
    const pointSelection = queryParams.get("point_selection");
    const createAlert = queryParams.get("create_alert");
    const sensorSelection = queryParams.get("sensor_selection");
    const openCreateDrawerParam = queryParams.get("open_create_drawer");

    if (
      !alertId &&
      !locationId &&
      !operationRuleLocationId &&
      !eventLocationId &&
      !pointSelection &&
      !createAlert &&
      !sensorSelection &&
      !openCreateDrawerParam
    ) {
      forecastChartPageStore.setIsAreaDrawingMode(false);
      forecastChartPageStore.setIsPointSelectionMode(false);
      forecastChartPageStore.clearSensorSelection();
      insightAlertStore.setCreateFormValues(null);
    }

    if (sensorSelection === "true") {
      forecastChartPageStore.setIsSensorSelectionMode(true);
      forecastChartPageStore.setIsAreaDrawingMode(false);
      forecastChartPageStore.setIsPointSelectionMode(false);
      // Copy insightAlertStore.selectedSensorIds to forecastChartPageStore as transient working set
      forecastChartPageStore.setSelectedSensorIds([
        ...insightAlertStore.selectedSensorIds,
      ]);
    }

    if (pointSelection === "true") {
      forecastChartPageStore.setIsPointSelectionMode(true);
      // Ensure area drawing mode is disabled when in point selection mode
      forecastChartPageStore.setIsAreaDrawingMode(false);
    }

    if (locationId) {
      forecastChartPageStore.setIsAreaDrawingMode(true);

      if (locationId !== "new") {
        const locationIdNumber = parseInt(locationId, 10);
        const existingLocation = locationStore.locations.find(
          (loc) => loc.id === locationIdNumber,
        );

        if (existingLocation) {
          locationStore.getLocationByIdAPI(locationIdNumber).catch((error) => {
            console.error(
              "Error fetching location data, using cached data:",
              error,
            );
            locationStore.setSelectedLocation(existingLocation);
            locationStore.setSelectedCoordinates(
              existingLocation.coordinates || [],
            );
          });
        } else {
          locationStore.getLocationByIdAPI(locationIdNumber).catch((error) => {
            console.error("Error fetching location data:", error);
            locationStore.getAllLocationsAPI().then(() => {
              const location = locationStore.locations.find(
                (loc) => loc.id === locationIdNumber,
              );
              if (location) {
                locationStore.setSelectedLocation(location);
                locationStore.setSelectedCoordinates(
                  location.coordinates || [],
                );
              }
            });
          });
        }
      }
    }

    // Handle event location from Event Manager (similar to operationRuleLocationId)
    // Only set area drawing mode if NOT in point selection mode
    if (eventLocationId && pointSelection !== "true") {
      forecastChartPageStore.setIsAreaDrawingMode(true);
    }

    if (operationRuleLocationId) {
      forecastChartPageStore.setIsAreaDrawingMode(true);
    }

    if (forecastChartPageStore.stations.length === 0) {
      const lastSelectedEnvironmentalType = localStorage.getItem(
        LAST_SELECTED_ENVIRONMENTAL_TYPE_KEY,
      ) as EnvironmentalType | null;

      forecastChartPageStore.getStationsForecastChartAPI(
        lastSelectedEnvironmentalType ?? EnvironmentalType.WEATHER,
      );
    }
  }, [
    forecastChartPageStore,
    insightAlertStore,
    location.search,
    unitMeasurementStore,
    locationStore,
  ]);

  useEffect(
    () => {
      forecastChartPageStore.setClickMapPointHeatMap([]);

      if (forecastChartPageStore.regionId > 0) {
        if (
          regionToMacroRegion(forecastChartPageStore.regionId) ===
          MacroRegion.RIVER_RHINE_GERMANY
        ) {
          forecastChartPageStore.setTabStateActivity(
            EnvironmentalType.WATER_FLOW,
          );
          setDefaultSelectedKeys([EnvironmentalType.WATER_FLOW]);
        }
      }
      if (
        regionStore.macroRegions.length === 1 &&
        forecastChartPageStore.regionId > 0
      ) {
        if (
          regionToMacroRegion(forecastChartPageStore.regionId) ===
          MacroRegion.RIVER_RHINE_GERMANY
        ) {
          setDisableMenuItems([
            EnvironmentalType.WAVE,
            EnvironmentalType.WEATHER,
          ]);
        } else if (
          regionToMacroRegion(forecastChartPageStore.regionId) ===
          MacroRegion.PORT_OF_PARANAGUA
        ) {
          setDisableMenuItems([EnvironmentalType.WATER_FLOW]);
        } else if (
          regionToMacroRegion(forecastChartPageStore.regionId) ===
          MacroRegion.PORT_OF_SINES
        ) {
          setDisableMenuItems([EnvironmentalType.WATER_FLOW]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [forecastChartPageStore.regionId],
  );

  useEffect(() => {
    if (preventTranslationOnRender?.current) {
      preventTranslationOnRender.current = false;
      return;
    }

    if (
      VisualizationType.SENSOR === forecastChartMeteogramStore.visualizationType
    ) {
      analyticsChartStore.setActionCallGetMeasuredChart();
    } else {
      forecastChartPageStore.setActionCallMeteogramChart(
        !forecastChartPageStore.actionCallMeteogramChart,
      );
    }

    forecastChartPageStore.setStationInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    const cancelToken = axios.CancelToken;
    const source = cancelToken.source();
    const controller = new AbortController();
    if (!forecastChartPageStore.clickMapLatLng) {
      forecastChartMeteogramStore.setOpenMeteogram(false);
    }

    forecastChartPageStore.updateMacroRegionOrigin(
      forecastChartPageStore.clickMapLatLng,
      regionStore.macroRegions,
    );

    if (!forecastChartPageStore.isChartRefreshed) {
      forecastChartMeteogramStore.setOpenMeteogram(true);
    } else {
      forecastChartPageStore.setIsChartRefreshed(false);
    }
    const pastDate = forecastChartMeteogramStore.selectForecastDateAvailable;
    const { macroRegionOrigin } = forecastChartPageStore;

    const isRhineRegion = macroRegionOrigin === MacroRegion.RIVER_RHINE_GERMANY;

    if (isRhineRegion || pastDate) {
      if (pastDate) {
        openMeteogramChart(source.token, pastDate.toUTC().toISO());
      } else {
        openMeteogramChart(source.token);
      }
    } else {
      openMeteogramChartMultipoints(controller);
    }

    const companyId = localStorage.getItem("companyId") || "0";

    const isVattenfall =
      forecastChartPageStore.macroRegionOrigin &&
      forecastChartPageStore.vattenfallRegions.includes(
        forecastChartPageStore.macroRegionOrigin,
      );

    const isBunkerOne = companyId === "90";

    const isWeather =
      forecastChartPageStore.tabStateActivty === EnvironmentalType.WEATHER;

    if ((isVattenfall || isBunkerOne) && isWeather) {
      forecastChartMeteogramStore.setSelectedEnvironmentalVariables(
        getEnvironmentalVariablesDefault({
          environmentalType: EnvironmentalType.WEATHER,
          isDefault100m: true,
        }),
      );
    }

    const isWave =
      forecastChartPageStore.tabStateActivty === EnvironmentalType.WAVE;

    if (isBunkerOne && isWave) {
      forecastChartMeteogramStore.setSelectedEnvironmentalVariables(
        getEnvironmentalVariablesDefault({
          environmentalType: EnvironmentalType.WAVE,
          addWavePower: true,
        }),
      );
    }

    return () => {
      source.cancel("Request cancelled");
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    forecastChartMeteogramStore.selectForecastDateAvailable,
    forecastChartPageStore.actionCallMeteogramChart,
    regionStore.macroRegions,
  ]);

  useEffect(() => {
    const controller = new AbortController();

    if (
      VisualizationType.SENSOR === forecastChartMeteogramStore.visualizationType
    ) {
      const coords = getCoordinatesIfValid();
      if (!coords) return;

      forecastChartPageStore.resetAllCharts();

      const selectedSensorMarker = isSensorMarkerSelected();

      if (selectedSensorMarker) {
        getMeasuredChart(controller);
      } else if (
        forecastChartPageStore.stateStationsWithSensorData === StoreState.DONE
      ) {
        getForecastChart(controller);
      }
    }
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    analyticsChartStore.actionCallGetMeasuredChart,
    forecastChartMeteogramStore.selectForecastDateAvailable,
  ]);

  useEffect(() => {
    const controller = new AbortController();

    const coords = getCoordinatesIfValid();
    if (!coords) return;

    const { macroRegionOrigin } = coords;

    if (!macroRegionOrigin) {
      forecastChartMeteogramStore.setState(StoreState.DONE);
      forecastChartMeteogramStore.setIsChartDataEmpty(true);
      forecastChartProbabilisticStore.setIsChartDataEmpty(true);
      analyticsChartStore.setIsChartDataEmpty(true);
      forecastChartProbabilisticStore.setShowNoProbabilisticDataMessage(false);
      return;
    }

    forecastChartMeteogramStore.setIsChartDataEmpty(false);
    analyticsChartStore.setIsChartDataEmpty(false);
    forecastChartProbabilisticStore.setIsChartDataEmpty(false);

    const selectedSensorMarker = isSensorMarkerSelected();

    const getChartFunction: {
      [key in VisualizationType]: (signal: AbortController) => void;
    } = {
      [VisualizationType.FORECAST]: getForecastChart,
      [VisualizationType.PROBABILISTIC]: getProbabilisticChart,
      [VisualizationType.SENSOR]: selectedSensorMarker
        ? getMeasuredChart
        : getForecastChart,
    };

    const getChart =
      getChartFunction[forecastChartMeteogramStore.visualizationType];

    if (getChart) {
      getChart(controller);
    }

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastChartPageStore.actionCallMapClick]);

  useEffect(() => {
    if (
      !toast.isActive(forecastChartPageStore.toastNoStationWithSensorId) &&
      forecastChartMeteogramStore.visualizationType ===
        VisualizationType.SENSOR &&
      forecastChartPageStore.messageToastNoStationWithSensor
    ) {
      toast({
        id: forecastChartPageStore.toastNoStationWithSensorId,
        title: forecastChartPageStore.messageToastNoStationWithSensor,
        status: "info",
        position: "top",
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forecastChartPageStore.messageToastNoStationWithSensor]);

  const handleHideWaveSpectraModal = () => {
    localStorage.setItem("showWaveSpectraModal", "false");
  };

  const openMeteogramChart = (cancelToken: CancelToken, create_at?: string) => {
    const stationId = forecastChartPageStore.selectedStation;
    const macroRegionOrigin = forecastChartPageStore.macroRegionOrigin;
    const station = forecastChartPageStore.getStationById(
      stationId,
      macroRegionOrigin!,
    );

    if (station) {
      forecastChartPageStore.updateMacroRegionOrigin(
        {
          lat: station.station_lat,
          lng: station.station_lon,
        },
        regionStore.macroRegions,
      );

      if (forecastChartPageStore.macroRegionOrigin) {
        forecastChartMeteogramStore.setIsChartDataEmpty(false);
        analyticsChartStore.setIsChartDataEmpty(false);
        forecastChartMeteogramStore.resetChartMeteogramData();
        forecastChartProbabilisticStore.setProbabilisticForecastData(null);
        if (
          VisualizationType.PROBABILISTIC ===
            forecastChartMeteogramStore.visualizationType &&
          forecastChartPageStore.probabilisticActivityVariable
        ) {
          forecastChartProbabilisticStore.setShowProbabilisticChart(true);
          forecastChartMeteogramStore.setShowMeteogram(false);
          forecastChartMeteogramStore.resetChartMeteogramData();
        } else if (
          VisualizationType.FORECAST ===
          forecastChartMeteogramStore.visualizationType
        ) {
          forecastChartMeteogramStore.setIsChartDataEmpty(false);
          forecastChartMeteogramStore.getChartMeteogramDataApi(
            station.station_id,
            forecastChartPageStore.tabStateActivty,
            cancelToken,
            forecastChartPageStore.macroRegionOrigin,
            create_at,
          );
          forecastChartMeteogramStore.setVisibleMeteogramChart(true);
        }
      } else {
        message.info(t("forecastChartPage"));
      }
    } else {
      forecastChartMeteogramStore.setState(StoreState.DONE);
      forecastChartMeteogramStore.setIsChartDataEmpty(true);
    }
  };

  const openMeteogramChartMultipoints = (controller: AbortController) => {
    const coords = getCoordinatesIfValid();
    if (!coords) return;

    const { macroRegionOrigin } = coords;

    if (!macroRegionOrigin) {
      forecastChartMeteogramStore.setState(StoreState.DONE);
      forecastChartMeteogramStore.setIsChartDataEmpty(true);
      forecastChartProbabilisticStore.setIsChartDataEmpty(true);
      forecastChartProbabilisticStore.setShowNoProbabilisticDataMessage(false);
      return;
    }

    forecastChartMeteogramStore.setIsChartDataEmpty(false);

    forecastChartPageStore.resetAllCharts();

    if (
      VisualizationType.PROBABILISTIC ===
      forecastChartMeteogramStore.visualizationType
    ) {
      if (forecastChartPageStore.probabilisticActivityVariable) {
        requestCancellationStore.cancelAllRequests().then(() => {
          getProbabilisticChart(controller);
        });

        forecastChartProbabilisticStore.setShowProbabilisticChart(true);
        forecastChartMeteogramStore.setShowMeteogram(false);
      } else {
        forecastChartProbabilisticStore.setShowNoProbabilisticDataMessage(true);
      }
    } else if (
      VisualizationType.FORECAST ===
      forecastChartMeteogramStore.visualizationType
    ) {
      getForecastChart(controller);

      forecastChartProbabilisticStore.setShowProbabilisticChart(false);
    }

    forecastChartMeteogramStore.setVisibleMeteogramChart(true);
  };

  const getForecastChart = (controller?: AbortController) => {
    const latLng = forecastChartPageStore.clickMapLatLng;
    const macroRegionOrigin = forecastChartPageStore.macroRegionOrigin;
    const stationId = forecastChartPageStore.selectedStation;

    forecastChartMeteogramStore.getEnvironmentalDataMeteogramNetCDFApi({
      latitude: latLng.lat,
      longitude: latLng.lng,
      environmental_type: forecastChartPageStore.tabStateActivty,
      macroRegionOrigin: macroRegionOrigin!,
      controller,
      stationId,
    });
    forecastChartMeteogramStore.getForecastUpdate(
      macroRegionOrigin!,
      forecastChartPageStore.tabStateActivty,
    );

    if (
      VisualizationType.SENSOR === forecastChartMeteogramStore.visualizationType
    ) {
      if (
        !toast.isActive(toastId) &&
        forecastChartMeteogramStore.openMeteogram &&
        forecastChartPageStore.stateStationsWithSensorData === StoreState.DONE
      ) {
        toast({
          id: toastId,
          title: t("sensorForecastData"),
          status: "info",
          position: "top",
          isClosable: true,
        });
      }
    }
  };

  const getProbabilisticChart = (controller: AbortController) => {
    const latLng = forecastChartPageStore.clickMapLatLng;
    const macroRegionOrigin = forecastChartPageStore.macroRegionOrigin;
    const probabilisticActivityVariable =
      forecastChartPageStore.probabilisticActivityVariable;

    forecastChartProbabilisticStore.getProbabilisticForecastDataApi(
      {
        latitude: latLng.lat,
        longitude: latLng.lng,
        environmentalVariable: probabilisticActivityVariable!,
        macroregionOrigin: macroRegionOrigin!,
      },
      controller,
    );
  };

  const getMeasuredChart = (controller?: AbortController) => {
    const selectedSensorMarker = isSensorMarkerSelected();

    const stationId = forecastChartPageStore.selectedStation;
    const regionId = forecastChartPageStore.regionId;
    analyticsChartStore.setIsChartDataEmpty(false);

    forecastChartMeteogramStore.setSelectedSensorMarker(selectedSensorMarker);

    const selectedPastDate =
      forecastChartMeteogramStore.selectForecastDateAvailable?.toUTC().toISO();

    analyticsChartStore.getMeasuredDataChartApi({
      environmentalType:
        selectedSensorMarker?.environmentalType ??
        forecastChartPageStore.tabStateActivty,
      stationId,
      regionId,
      createdAt: selectedPastDate,
      controller,
    });
  };

  const getCoordinatesIfValid = () => {
    const latLng = forecastChartPageStore.clickMapLatLng;

    if (!latLng) return false;

    if (!forecastChartPageStore.macroRegionOrigin) {
      forecastChartMeteogramStore.setState(StoreState.DONE);
      forecastChartMeteogramStore.setIsChartDataEmpty(true);
      return false;
    }

    const isLatLonValidGrid = forecastChartPageStore.validateLatLonGrid(
      latLng.lat,
      latLng.lng,
      forecastChartPageStore.macroRegionOrigin,
    );

    if (!isLatLonValidGrid) {
      forecastChartPageStore.resetAllCharts();
      forecastChartMeteogramStore.setState(StoreState.DONE);
      forecastChartMeteogramStore.setIsChartDataEmpty(true);
      forecastChartProbabilisticStore.setShowNoProbabilisticDataMessage(false);
      return false;
    }

    return {
      latLng,
      macroRegionOrigin: forecastChartPageStore.macroRegionOrigin,
    };
  };

  const isSensorMarkerSelected = () => {
    const isSensorMarker = forecastChartPageStore.sensorMarkerData?.find(
      (s) =>
        s.region_id === forecastChartPageStore.regionId &&
        s.station_id === forecastChartPageStore.selectedStation,
    );
    forecastChartMeteogramStore.setIsSensorMarker(!!isSensorMarker);

    return isSensorMarker;
  };

  return (
    <Box h="92.2vh" className="forecast-chart-container">
      <Box h={"88vh"}>
        {!forecastChartPageStore.isAreaDrawingMode &&
        !forecastChartPageStore.isPointSelectionMode &&
        !forecastChartPageStore.isSensorSelectionMode ? (
          <>
            {isOpen && (
              <EventCreateDrawer
                onClose={() => {
                  setDrawerInitialStep(0);
                  onClose();
                }}
                isOpen={isOpen}
                showPhoneVerificationModal={!isPhoneAlreadyVerified}
                onPhoneModalOpen={onPhoneModalOpen}
                initialStep={drawerInitialStep}
              />
            )}

            <PhoneVerificationModal
              isOpen={isPhoneModalOpen}
              onClose={onPhoneModalClose}
              onSuccess={handlePhoneUpdateSuccess}
              initialPhoneData={phoneData}
              title={tPhoneModal("updatePhoneNumber") as string}
              description={tPhoneModal("verifyPhoneToReceiveAlerts") as string}
              confirmButtonText={tPhoneModal("sendVerificationCode") as string}
              isPhoneAlreadyVerified={isPhoneAlreadyVerified}
            />

            <Box position="relative">
              <I4castAlert
                visible={forecastChartPageStore.state === StoreState.SUCCESS}
                message={forecastChartPageStore.messageSuccess}
                type="success"
              />

              <I4castAlert
                visible={forecastChartPageStore.state === StoreState.ERROR}
                message={forecastChartPageStore.messageError}
                type="error"
              />

              <Box display="grid" position={"relative"}>
                <ForecastChartPanelHeader />

                <Flex
                  position="absolute"
                  zIndex={9999999}
                  borderRadius="8px"
                  bg="white"
                  color="#313F4D"
                  align="center"
                  px={2}
                  py="6px"
                  mt="7px"
                  left={showInSmallScreen ? "50px" : "330px"}
                  right="auto"
                  h="52px"
                  justify="space-between"
                  boxShadow="md"
                  cursor="pointer"
                  gap={2}
                  onClick={onOpen}
                  top={showInSmallScreen ? "125px" : "auto"}
                >
                  <Flex align="center" gap={2}>
                    <Flex
                      align="center"
                      p="8px"
                      bg="#0884CC"
                      gap={10}
                      borderRadius="8px"
                    >
                      <img
                        src={Megaophone}
                        alt="megaphone"
                        width={16}
                        height={16}
                      />
                    </Flex>
                    <Text
                      fontSize="12px"
                      mt="2px"
                      textColor="#0884CC"
                      fontWeight={500}
                      lineHeight="normal"
                    >
                      {t("createEvent")}
                    </Text>
                  </Flex>

                  <ChevronRight size="18px" color="#A2B3C4" />
                </Flex>

                {forecastChartPageStore.insightMode &&
                  !!forecastChartPageStore.selectedStation && (
                    <ForecastChartInsightTimeline />
                  )}

                <ForecastChartPanelEnvironmentalButtons
                  key={`menuButtonEnvironmental${
                    defaultSelectedKeys && defaultSelectedKeys[0]
                  }`}
                  defaultSelectedKeys={defaultSelectedKeys}
                  disableMenuItems={disableMenuItems}
                />
              </Box>

              <Box>
                <Spin
                  spinning={
                    forecastChartPageStore.state === StoreState.PEDDING ||
                    forecastChartPageStore.stateStationsWithSensorData ===
                      StoreState.PEDDING
                  }
                >
                  <ForecastChartPanelMapContainer />
                </Spin>

                <ForecastChartPanelMeteogram />
              </Box>
            </Box>
          </>
        ) : forecastChartPageStore.isPointSelectionMode ? (
          <>
            <Box>
              <Spin
                spinning={
                  forecastChartPageStore.state === StoreState.PEDDING ||
                  forecastChartPageStore.stateStationsWithSensorData ===
                    StoreState.PEDDING
                }
              >
                <ForecastChartPanelMapContainer />
              </Spin>
            </Box>
          </>
        ) : forecastChartPageStore.isSensorSelectionMode ? (
          <>
            <Box>
              <ForecastChartPanelMapContainer />
            </Box>
          </>
        ) : (
          <>
            <Box>
              <Spin
                spinning={
                  forecastChartPageStore.state === StoreState.PEDDING ||
                  forecastChartPageStore.stateStationsWithSensorData ===
                    StoreState.PEDDING
                }
              >
                <ForecastChartPanelMapContainer />
              </Spin>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default observer(ForecastChartPage);
